/**
 * Keyboard shortcuts — bound to a container, not window globally by default.
 *
 * @param {object} engine PlaybackEngine instance
 * @param {HTMLElement} target
 * @param {object} [options]
 * @returns {function} dispose
 */
export function bindKeyboardShortcuts(engine, target, options) {
  options = options || {};
  if (!engine || !target || options.enabled === false) {
    return function () {};
  }

  var seekStep = options.seekStep != null ? options.seekStep : 10;
  var volumeStep = options.volumeStep != null ? options.volumeStep : 0.05;
  var frameStep = options.frameStep != null ? options.frameStep : 1 / 30;
  var bus = options.bus || null;
  /** Optional transport facade (e.g. CastingManager) — same surface as engine */
  var transport = options.transport || engine;
  var skipSpatialArrows = !!options.skipSpatialArrows;

  function getState() {
    if (typeof options.getState === "function") {
      return options.getState();
    }
    return engine.getState();
  }

  function onKeyDown(e) {
    var tag = (e.target && e.target.tagName) || "";
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") {
      return;
    }

    var st = getState();
    switch (e.key) {
      case " ":
      case "k":
      case "K":
        e.preventDefault();
        if (st.playbackState === "playing") {
          transport.pause();
        } else {
          transport.play();
        }
        break;
      case "ArrowLeft":
        if (skipSpatialArrows) break;
        e.preventDefault();
        transport.seek((st.currentTime || 0) - seekStep);
        break;
      case "j":
      case "J":
        e.preventDefault();
        transport.seek((st.currentTime || 0) - seekStep);
        break;
      case "ArrowRight":
        if (skipSpatialArrows) break;
        e.preventDefault();
        transport.seek((st.currentTime || 0) + seekStep);
        break;
      case "l":
      case "L":
        e.preventDefault();
        transport.seek((st.currentTime || 0) + seekStep);
        break;
      case "ArrowUp":
        if (skipSpatialArrows) break;
        e.preventDefault();
        transport.setVolume(Math.min(1, (st.volume || 0) + volumeStep));
        break;
      case "ArrowDown":
        if (skipSpatialArrows) break;
        e.preventDefault();
        transport.setVolume(Math.max(0, (st.volume || 0) - volumeStep));
        break;
      case "m":
      case "M":
        e.preventDefault();
        transport.setMuted(!st.muted);
        break;
      case "f":
      case "F":
        e.preventDefault();
        engine.toggleFullscreen(target);
        break;
      case "p":
      case "P":
        e.preventDefault();
        engine.togglePictureInPicture(target).catch(function () {});
        break;
      case "c":
      case "C":
        e.preventDefault();
        if (bus) {
          bus.emit("ui:toggleSubtitles");
        }
        break;
      case ",":
        e.preventDefault();
        engine.pause();
        engine.seek(Math.max(0, (st.currentTime || 0) - frameStep));
        break;
      case ".":
        e.preventDefault();
        engine.pause();
        engine.seek((st.currentTime || 0) + frameStep);
        break;
      case "N":
        e.preventDefault();
        if (bus) bus.emit("ui:nextChapter");
        break;
      case "B":
        e.preventDefault();
        if (bus) bus.emit("ui:prevChapter");
        break;
      default:
        break;
    }
  }

  target.tabIndex = target.tabIndex >= 0 ? target.tabIndex : 0;
  target.setAttribute("role", target.getAttribute("role") || "application");
  target.addEventListener("keydown", onKeyDown);

  return function dispose() {
    target.removeEventListener("keydown", onKeyDown);
  };
}

export default bindKeyboardShortcuts;
